import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Badge,
  Card,
  FloatingSave,
  Modal,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import {
  CommandAdminApi,
  commandStoreApi,
  InspectionGradeCode,
  inspectionGradeCodeNames,
} from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';
import { StorePage } from '../StorePage';
import { EmailSettingsCard } from './EmailSettingsCard';
import { ExtensionHealthCard } from './ExtensionHealthCard';
import { FeatureFlagsCard } from './FeatureFlagsCard';
import { FrontSettingsCard } from './FrontSettingsCard';

type Store = CommandAdminApi.ResponseBody<'/store/:storeId', 'get'>;

type InspectionGrade = Store['inspectionGrades'][number];

const createSchema =
  commandStoreApi['/store/:storeId/inspection-grade'].post.request.body;

const updateSchema =
  commandStoreApi['/store/:storeId/inspection-grade/:inspectionGradeId'].put
    .request.body;

const { idColumn } = sharedTableDefinitions;

function AddInspectionGradeModal({
  availableCodes,
  defaultCode,
  onClose,
}: {
  availableCodes: InspectionGradeCode[];
  defaultCode: InspectionGradeCode;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', code: defaultCode },
  });

  const { mutate: createInspectionGrade } = useMutation({
    mutationFn: api.store(storeId).createInspectionGrade,
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Created inspection grade');
    },
    onError: () => toast.show('Failed to create inspection grade'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => createInspectionGrade(body))(event).catch(
      console.error,
    );
  };

  const codeOptions = availableCodes.map((code) => ({
    value: code,
    label: inspectionGradeCodeNames[code],
  }));

  return (
    <Modal show onClose={onClose} title="Add Inspection Grade">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Controller
          control={control}
          name="code"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Grade"
              required
              options={codeOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <TextInput
          id="name"
          label="Name"
          required
          {...register('name')}
          error={errors.name?.message}
        />
        <div className="flex justify-end">
          <Action variant="primary" type="submit">
            Add
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function EditInspectionGradeModal({
  inspectionGrade,
  onClose,
}: {
  inspectionGrade: InspectionGrade;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(updateSchema),
    defaultValues: { name: inspectionGrade.name },
  });

  const { mutate: updateInspectionGrade } = useMutation({
    mutationFn: (body: z.infer<typeof updateSchema>) =>
      api.store(storeId).updateInspectionGrade(inspectionGrade.id, body),
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Updated inspection grade');
    },
    onError: () => toast.show('Failed to update inspection grade'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => updateInspectionGrade(body))(event).catch(
      console.error,
    );
  };

  return (
    <Modal
      show
      onClose={onClose}
      title={`Edit ${inspectionGradeCodeNames[inspectionGrade.code]} Grade`}
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Name"
          required
          {...register('name')}
          error={errors.name?.message}
        />
        <div className="flex justify-end">
          <Action variant="primary" type="submit">
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function InspectionGradesCard() {
  const { inspectionGrades } = useStoreContext();
  const [showAdd, setShowAdd] = useState(false);
  const [gradeToEdit, setGradeToEdit] = useState<InspectionGrade | null>(null);

  const availableCodes = Object.values(InspectionGradeCode).filter(
    (code) => !inspectionGrades.some((grade) => grade.code === code),
  );
  const [defaultCode] = availableCodes;

  const columns: ColumnDef<InspectionGrade>[] = [
    idColumn<InspectionGrade>({ meta: { align: 'left' } }),
    { accessorKey: 'name', header: 'Name', enableSorting: true },
    {
      accessorKey: 'code',
      header: 'Grade',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge>{inspectionGradeCodeNames[row.original.code]}</Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Action
          variant="ghost"
          icon="edit"
          accessibilityLabel="Edit Inspection Grade"
          onClick={() => setGradeToEdit(row.original)}
        />
      ),
    },
  ];

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Inspection Grades</Card.Heading>
        <Action
          variant="ghost"
          icon="add"
          accessibilityLabel="Add Inspection Grade"
          disabled={availableCodes.length === 0}
          onClick={() => setShowAdd(true)}
        />
      </Card.Line>
      {inspectionGrades.length > 0 ? (
        <DataTable data={inspectionGrades} columns={columns} pageSize={10} />
      ) : (
        <DataTableStatus
          icon="tag"
          title="No inspection grades"
          details="Inspection grades will appear here once they sync from the store."
        />
      )}

      {showAdd && defaultCode && (
        <AddInspectionGradeModal
          availableCodes={availableCodes}
          defaultCode={defaultCode}
          onClose={() => setShowAdd(false)}
        />
      )}

      {gradeToEdit && (
        <EditInspectionGradeModal
          inspectionGrade={gradeToEdit}
          onClose={() => setGradeToEdit(null)}
        />
      )}
    </Card>
  );
}

const formId = 'platform-settings';

const platformSettingsSchema = z.object({
  ...commandStoreApi['/store/:storeId/front-settings'].put.request.body.shape,
  ...commandStoreApi['/store/:storeId/feature-flags'].put.request.body.shape,
  ...commandStoreApi['/store/:storeId/email-settings'].put.request.body.shape,
});

export type PlatformSettings = z.infer<typeof platformSettingsSchema>;

export default function PlatformPage() {
  const store = useStoreContext();
  const refetch = useRefetchStore();
  const toast = useToast();

  const form = useForm({
    resolver: zodResolver(platformSettingsSchema),
    values: {
      frontReportTags: store.frontReportTags,
      featureFlags: store.featureFlags,
      verifiedEmailDomain: store.verifiedEmailDomain,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = form;

  const { mutateAsync: save } = useMutation({
    // each card is its own endpoint, so only the edited sections are sent
    mutationFn: async ({
      frontReportTags,
      featureFlags,
      verifiedEmailDomain,
    }: PlatformSettings) => {
      if (dirtyFields.frontReportTags)
        await api.store(store.id).updateFrontSettings({ frontReportTags });

      if (dirtyFields.featureFlags)
        await api.store(store.id).updateFeatureFlags({ featureFlags });

      if (dirtyFields.verifiedEmailDomain)
        await api.store(store.id).updateEmailSettings({ verifiedEmailDomain });
    },
    onSuccess: () => {
      refetch();
      toast.show('Updated platform settings');
    },
    onError: () => toast.show('Failed to update platform settings'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => save(values))(event).catch(console.error);
  };

  return (
    <StorePage title="Platform">
      <div className="flex flex-col gap-4">
        <ExtensionHealthCard />

        {/* cards with their own modals stay outside the form — a modal's submit
            bubbles through the React tree into the page form */}
        <InspectionGradesCard />

        <FormProvider {...form}>
          <form
            id={formId}
            className="flex flex-col gap-4"
            onSubmit={submitHandler}
            onReset={(event) => {
              event.preventDefault();
              reset();
            }}
          >
            <FloatingSave
              isDirty={isDirty}
              isSubmitting={isSubmitting}
              form={formId}
            />

            <FeatureFlagsCard />
            <EmailSettingsCard />
            <FrontSettingsCard />
          </form>
        </FormProvider>
      </div>
    </StorePage>
  );
}
